// §6.1 — the remaining house chrome. Each of these is filled in by its own milestone:
// NotificationHost and TrayFlyout at M10, LoginScreen and LockScreen at M12.9. The Switcher
// moved into its own file at M6.4, where it stopped being a placeholder.
// They exist here from M3 because `validateSkin` requires the full component set (§6.1) and
// because a skin with a hole in it is not a skin.
import { useEffect } from 'react';
import { COPY } from '@/copy';
import { beginSignIn } from '@/kernel/server';
import { useKernel, useNotifications, useStoreValue } from '@/kernel/react';

/**
 * §6.3 — bottom-right and quiet.
 *
 * Two sources, one place: §13's notifications, whose 6 s life the kernel owns, and `shellui`'s
 * single shell-authored toast (§6.4's stolen-chord message, a skipped session), which belongs to
 * nobody's app and never reaches the Center.
 */
export function NotificationHost() {
  const kernel = useKernel();
  const toast = useStoreValue(kernel.shellui.store, (s) => s.toast);
  const { toasts } = useNotifications();

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => kernel.shellui.dismissToast(), 6_000);
    return () => clearTimeout(timer);
  }, [toast, kernel]);

  if (!toast && toasts.length === 0) return null;
  return (
    <div className="house-toasts" data-testid="notification-host">
      {toasts.map((item) => (
        <div
          key={item.id}
          className="house-toast"
          role="status"
          data-testid={`notification-toast-${item.id}`}
          data-app-id={item.appId}
        >
          {/* §13 — clicking the body focuses the window that sent it. */}
          <button
            type="button"
            className="house-toast-body"
            data-testid={`notification-toast-open-${item.id}`}
            onClick={() => kernel.notify.activate(item.id)}
          >
            <span className="house-toast-title">{item.title}</span>
            {item.body !== undefined && item.body.length > 0 && (
              <span className="house-toast-text">{item.body}</span>
            )}
          </button>
          {item.actions.map((action) => (
            <button
              key={action}
              type="button"
              className="house-toast-action"
              data-testid={`notification-toast-action-${item.id}-${action}`}
              onClick={() => kernel.notify.act(item.id, action)}
            >
              {action}
            </button>
          ))}
          <button
            type="button"
            className="house-toast-dismiss"
            aria-label={COPY.chrome.dismiss}
            onClick={() => kernel.notify.dismissToast(item.id)}
          >
            ×
          </button>
        </div>
      ))}
      {toast && (
        <div className="house-toast" role="status" data-testid="shell-toast">
          <span data-testid="shell-toast-message">{toast.message}</span>
          <button
            type="button"
            className="house-toast-dismiss"
            aria-label={COPY.chrome.dismiss}
            onClick={() => kernel.shellui.dismissToast()}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * §6.3 — the house skin has no tray flyout: its bar opens the Center itself, and §13's Center is
 * one component every skin mounts (D-140). M13.6 gives the tray a clock and calendar.
 */
export function TrayFlyout() {
  return null;
}

/** §8.3 — the whole page when an admin has turned anonymous access off (M12.9). */
export function LoginScreen() {
  return (
    <div className="house-login" data-testid="login-screen">
      <div className="house-login-card">
        <h1 className="house-login-title">{COPY.loginTitle}</h1>
        <p className="house-login-body">{COPY.loginBody}</p>
        <button
          type="button"
          className="house-login-action"
          data-testid="login-signin"
          onClick={() => beginSignIn(() => undefined)}
        >
          {COPY.loginAction}
        </button>
      </div>
    </div>
  );
}

/** §6.4 — the lock screen. */
export function LockScreen() {
  const kernel = useKernel();
  const locked = useStoreValue(kernel.shellui.store, (s) => s.overlay === 'lock');
  if (!locked) return null;
  return (
    <div className="house-lock" data-testid="lock-screen" onClick={() => kernel.shellui.closeOverlay()} />
  );
}
